// ============================================================================
// Message cache — IndexedDB node store for instant session restore
// ============================================================================
// Provides the low-level IndexedDB operations: open, cacheNode, evictLRU,
// restoreCachedNodes, clearCachedSession. The higher-level cache state
// (nodeOrderMap, sessionMinOrder, sessionMaxOrder) and DOM-bound functions
// (cachePageNodes, cacheVisibleNodes, trimDomMessages) remain in main.js
// because they reference DOM elements and session state.

const DB_NAME = "devin-gw-cache";
const DB_VERSION = 2;
const NODE_STORE = "nodes";
export const NODE_CACHE_CAP = 5000;

let msgDB = null;

export function openMsgDB() {
  return new Promise((resolve) => {
    if (msgDB) { resolve(msgDB); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Drop the legacy single-blob store from v1.
      if (db.objectStoreNames.contains("messages")) {
        try { db.deleteObjectStore("messages"); } catch {}
      }
      if (!db.objectStoreNames.contains(NODE_STORE)) {
        const store = db.createObjectStore(NODE_STORE, { keyPath: "key" });
        store.createIndex("sessionId", "sessionId", { unique: false });
        store.createIndex("ts", "ts", { unique: false });
        store.createIndex("order", ["sessionId", "order"], { unique: false });
      }
    };
    req.onsuccess = () => { msgDB = req.result; resolve(msgDB); };
    req.onerror = () => resolve(null);
  });
}

export function nodeKey(sessionId, nodeId) { return sessionId + ":" + nodeId; }

// Write one node's rendered HTML to the cache with a logical order.
export async function cacheNode(sessionId, nodeId, html, order, orderMap) {
  if (!nodeId || !html) return;
  const db = await openMsgDB(); if (!db) return;
  const key = nodeKey(sessionId, nodeId);
  try {
    const tx = db.transaction(NODE_STORE, "readwrite");
    tx.objectStore(NODE_STORE).put({
      key, sessionId, nodeId, html, order, ts: Date.now(),
    });
    if (orderMap) orderMap.set(nodeId, order);
  } catch {}
  scheduleEvict(sessionId);
}

let evictTimer = null;
let evictSessionId = null;
function scheduleEvict(sessionId) {
  evictSessionId = sessionId;
  if (evictTimer) return;
  evictTimer = setTimeout(() => { evictTimer = null; void evictLRU(evictSessionId); }, 1000);
}

export async function evictLRU(sessionId) {
  const db = await openMsgDB(); if (!db) return;
  try {
    const tx = db.transaction(NODE_STORE, "readwrite");
    const idx = tx.objectStore(NODE_STORE).index("sessionId");
    const countReq = idx.count(sessionId);
    await new Promise((res) => { countReq.onsuccess = res; countReq.onerror = res; });
    const count = countReq.result;
    if (count <= NODE_CACHE_CAP) return;
    const toRemove = count - NODE_CACHE_CAP;
    const tsIdx = tx.objectStore(NODE_STORE).index("ts");
    let removed = 0;
    await new Promise((res) => {
      const cur = tsIdx.openCursor(); // ascending ts = least-recently-used first
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c || removed >= toRemove) { res(); return; }
        if (c.value.sessionId === sessionId) { c.delete(); removed++; }
        c.continue();
      };
      cur.onerror = () => res();
    });
  } catch {}
}

// Load all cached nodes for a session, ordered oldest→newest.
export async function restoreCachedNodes(sessionId) {
  const db = await openMsgDB(); if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(NODE_STORE, "readonly");
      const idx = tx.objectStore(NODE_STORE).index("order");
      const out = [];
      const range = IDBKeyRange.bound([sessionId, -1e15], [sessionId, 1e15]);
      const cur = idx.openCursor(range);
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) { resolve(out); return; }
        out.push({ nodeId: c.value.nodeId, html: c.value.html });
        c.continue();
      };
      cur.onerror = () => resolve(out);
    } catch { resolve([]); }
  });
}

export async function clearCachedSession(sessionId) {
  const db = await openMsgDB(); if (!db) return;
  try {
    const tx = db.transaction(NODE_STORE, "readwrite");
    const idx = tx.objectStore(NODE_STORE).index("sessionId");
    await new Promise((res) => {
      const cur = idx.openCursor(sessionId);
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) { res(); return; }
        c.delete(); c.continue();
      };
      cur.onerror = () => res();
    });
  } catch {}
}
